/**
 * 就地文本编辑（覆盖一个 HTML `<textarea>`，不往 SVG 里塞 foreignObject）。
 *
 * 三条纪律：
 *  1) 输入过程**直接写回 DOM**（实时预览、看得见效果），但不进历史
 *  2) 退出（Esc / 失焦 / Ctrl+Enter / 单行时 Enter）时：先回滚预览 → 再用**一条**命令提交
 *     ★ 因此"一次编辑 = 恰好 1 条 undo"，且 redo 也能正确重放
 *  3) IME：组合期间不提交；Enter 在组合中不当作"结束编辑"
 */
import type { Command } from '@/core/history';
import { makeStructuralPatch, setInnerXml, snapshotOf } from '@/core/patch';
import { writeTextLines } from '@/core/ops/text';
import {
  LINE_HEIGHT_RATIO,
  VERTICAL_RATIO,
  isTextLike,
  readTextProps,
  textOwner,
} from '@/core/query/text';

export interface TextEditDeps {
  host: HTMLElement;
  execute(cmd: Command): boolean;
  syncOverlay(): void;
  /** 元素在屏幕上的位置（生产用 getBoundingClientRect；测试注入） */
  rectOf(el: Element): DOMRect;
  zoom(): number;
  onEditingChange?(el: Element | null): void;
}

export interface TextEditHandle {
  begin(el: Element): boolean;
  commit(): boolean;
  editing(): Element | null;
  detach(): void;
}

export function createTextEdit(deps: TextEditDeps): TextEditHandle {
  let editing: Element | null = null;
  let box: HTMLTextAreaElement | null = null;
  let snapshot = '';
  let lineHeight = 20;
  let step = 20;
  let singleLine = true;
  let vertical = false;
  let composing = false;
  let closing = false;

  /**
   * 编辑框**跟着内容重新贴合**。
   *
   * 为什么需要：编辑框宽度原本等于打开那一刻的文字 bbox（"恰好覆盖"），
   * 而透明底之后这个尺寸就是用户看到的全部 —— 字一多，框不够宽就会**软换行**（"文字"被折成竖排），
   * 字一少又显得空。所以在每次内容变化后按新的 bbox 重新贴合（text-anchor: middle 时框会跟着居中位移）。
   */
  const refit = (): void => {
    if (!editing || !box) return;
    const hostRect = deps.host.getBoundingClientRect();
    const zoom = deps.zoom();
    const props = readTextProps(editing);
    const rect = deps.rectOf(editing);
    box.style.left = `${rect.left - hostRect.left}px`;
    box.style.top = `${rect.top - hostRect.top}px`;
    box.style.width = `${Math.max(30, rect.width || props.fontSize * zoom * 4)}px`;
    box.style.height = `${Math.max(props.fontSize * zoom, rect.height)}px`;
    box.style.fontSize = `${props.fontSize * zoom}px`;
    box.style.fontFamily = props.fontFamily;
  };

  const preview = (value: string): void => {
    if (!editing) return;
    if (vertical) {
      const columnX = Number(editing.getAttribute('x') ?? '0') || 0;
      const glyphs = [...value.replace(/\n/g, '')];
      writeTextLines(editing, glyphs.length > 0 ? glyphs : [''], step, columnX);
      refit();
      return;
    }
    writeTextLines(editing, value.split('\n'), lineHeight);
    refit();
  };

  const teardown = (): void => {
    // 摘掉"编辑中"标记 → 原文恢复显示（这是**编辑器私有属性**，导出会被剥掉，不会落进文件）
    editing?.removeAttribute('data-editor-editing');
    if (box) {
      box.removeEventListener('input', onInput);
      box.removeEventListener('keydown', onKey);
      box.removeEventListener('blur', onBlur);
      box.removeEventListener('compositionstart', onCompositionStart);
      box.removeEventListener('compositionend', onCompositionEnd);
      box.remove();
      box = null;
    }
    editing = null;
    deps.onEditingChange?.(null);
  };

  function onInput(): void {
    if (box) preview(box.value);
  }

  function onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation(); // 编辑框独占 Esc：别让全局快捷键同时"退出分组/清空选中"
      commit();
      return;
    }
    if (e.key !== 'Enter' || e.isComposing || composing) return;
    const forceCommit = e.ctrlKey || e.metaKey || e.shiftKey || singleLine;
    if (!forceCommit) return; // 多行文本：Enter 换行，Ctrl+Enter 才结束编辑
    e.preventDefault();
    e.stopPropagation();
    commit();
  }

  function onBlur(): void {
    commit();
  }

  function onCompositionStart(): void {
    composing = true;
  }

  function onCompositionEnd(): void {
    composing = false;
  }

  function commit(): boolean {
    if (!editing || closing) return false;
    closing = true;
    const el = editing;
    const finalXml = snapshotOf(el);
    let applied = false;

    if (finalXml !== snapshot) {
      const before = snapshot;
      // 先回滚预览，再让命令把最终形态应用一次（与拖动提交同一套路）
      setInnerXml(el, before);
      applied = deps.execute({
        label: '文字',
        patches: [
          makeStructuralPatch(el, before, '文字', () => {
            if (snapshotOf(el) !== finalXml) setInnerXml(el, finalXml);
          }),
        ],
      });
    }

    teardown();
    closing = false;
    deps.syncOverlay();
    return applied;
  }

  function begin(raw: Element): boolean {
    const el = textOwner(raw) ?? raw;
    if (!isTextLike(el)) return false;
    if (editing) commit();

    const props = readTextProps(el);
    snapshot = snapshotOf(el);
    lineHeight = props.fontSize * LINE_HEIGHT_RATIO;
    step = props.fontSize * VERTICAL_RATIO;
    vertical = props.vertical;
    singleLine = !vertical && props.lines.length <= 1;
    editing = el;
    // 编辑期间**隐藏 SVG 原文**：否则 HTML 编辑框的文字和底下的原文会重影（用户反馈过）
    el.setAttribute('data-editor-editing', '');

    const rect = deps.rectOf(el);
    const hostRect = deps.host.getBoundingClientRect();
    const zoom = deps.zoom();
    const doc = deps.host.ownerDocument;
    const area = doc.createElement('textarea');
    area.className = 'sve-text-edit';
    area.dataset.editing = '1';
    area.spellcheck = false;
    area.value = props.lines.join('\n');
    area.style.left = `${rect.left - hostRect.left}px`;
    area.style.top = `${rect.top - hostRect.top}px`;
    area.style.width = `${Math.max(30, rect.width || props.fontSize * zoom * 4)}px`;
    area.style.height = `${Math.max(props.fontSize * zoom, rect.height)}px`;
    area.style.fontSize = `${props.fontSize * zoom}px`;
    area.style.fontFamily = props.fontFamily;
    area.style.fontWeight = props.fontWeight;
    area.style.fontStyle = props.fontStyle;
    area.style.color = props.fill;
    area.style.textAlign =
      props.textAnchor === 'middle' ? 'center' : props.textAnchor === 'end' ? 'right' : 'left';

    area.addEventListener('input', onInput);
    area.addEventListener('keydown', onKey);
    area.addEventListener('blur', onBlur);
    area.addEventListener('compositionstart', onCompositionStart);
    area.addEventListener('compositionend', onCompositionEnd);

    deps.host.appendChild(area);
    box = area;
    area.focus?.();
    // 不整段选中：把光标收到末尾（放在延后一帧做，是为了盖掉浏览器双击默认的"选中一个词"）。
    // 参考产品也是"光标落原地"，不是全选高亮。
    const end = area.value.length;
    area.setSelectionRange?.(end, end);
    if (typeof setTimeout === 'function') {
      setTimeout(() => {
        if (box === area) area.setSelectionRange?.(end, end);
      }, 0);
    }
    deps.onEditingChange?.(el);
    return true;
  }

  return {
    begin,
    commit,
    editing: () => editing,
    detach() {
      teardown();
    },
  };
}
